import { Socket } from 'socket.io-client';
import { BaseSocketService, SocketConnectionConfig } from './baseSocketService';
import { MerchantCashAdvance } from '../graphql/types';

export interface McaEvent {
  type: 'created' | 'updated' | 'deleted';
  mca: MerchantCashAdvance;
}

export interface McaCallbacks {
  onCreated?: (event: McaEvent) => void;
  onUpdated?: (event: McaEvent) => void;
  onDeleted?: (event: McaEvent) => void;
}

export class McaSocketService extends BaseSocketService {
  private listeners: Map<string, Map<string, Function>> = new Map();
  private subscriptions: Map<string, number> = new Map();

  constructor() {
    super('McaUpdates');
  }

  async subscribeTo(mcaId: string | null, config: SocketConnectionConfig, callbacks: McaCallbacks): Promise<void> {
    const socket = await this.getSocket(config);

    if (this.subscriptions.size === 0) {
      socket.on('exception', (error) => {
        this.logger.error('Server exception:', error);
      });
    }

    const subscriptionKey = mcaId || 'all';
    const currentCount = this.subscriptions.get(subscriptionKey) || 0;

    if (currentCount === 0) {
      const subscribeData = mcaId ? { mcaId } : { subscribeToAll: true };

      await new Promise<void>((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          reject(new Error('Subscription request timed out'));
        }, 5000);

        socket.once('subscribed', () => {
          clearTimeout(timeoutId);
          resolve();
        });

        socket.once('error', (error) => {
          clearTimeout(timeoutId);
          reject(error);
        });

        socket.emit('subscribe', subscribeData);
      });
    }

    this.subscriptions.set(subscriptionKey, currentCount + 1);
    if (currentCount === 0) {
      this.incrementSubscriptions();
    }
    this.setupListeners(socket, mcaId, callbacks, subscriptionKey);
  }

  async unsubscribe(mcaId: string | null): Promise<void> {
    const subscriptionKey = mcaId || 'all';
    const currentCount = this.subscriptions.get(subscriptionKey) || 0;

    if (currentCount <= 1) {
      this.subscriptions.delete(subscriptionKey);
      this.decrementSubscriptions();
      this.removeListeners(subscriptionKey);

      if (this.socket?.connected) {
        const unsubscribeData = mcaId ? { mcaId } : { unsubscribeFromAll: true };
        await this.emitWithAuth('unsubscribe', unsubscribeData, { namespace: 'mca-updates', userId: localStorage.getItem('userId') || '' });
      }
    } else {
      this.subscriptions.set(subscriptionKey, currentCount - 1);
      this.removeListeners(subscriptionKey);
    }
  }

  protected onReconnection(): void {
    if (this.socket && this.subscriptions.size > 0) {
      this.subscriptions.forEach((_, subscriptionKey) => {
        const mcaId = subscriptionKey === 'all' ? null : subscriptionKey;
        const subscribeData = mcaId ? { mcaId } : { subscribeToAll: true };
        this.emitWithAuth('subscribe', subscribeData, { namespace: 'mca-updates', userId: localStorage.getItem('userId') || '' });
      });
    }
  }

  private setupListeners(socket: Socket, mcaId: string | null, callbacks: McaCallbacks, subscriptionKey: string): void {
    if (!this.listeners.has(subscriptionKey)) {
      this.listeners.set(subscriptionKey, new Map());
    }
    const mcaListeners = this.listeners.get(subscriptionKey)!;

    if (callbacks.onCreated) {
      const handler = (data: McaEvent) => {
        if (!data?.mca) return;
        if (mcaId && data.mca.id !== mcaId) return;
        callbacks.onCreated!(data);
      };
      socket.on('mca:created', handler);
      mcaListeners.set('mca:created', handler);
    }

    if (callbacks.onUpdated) {
      const handler = (data: McaEvent) => {
        if (!data?.mca) {
          this.logger.debug('Received mca:updated event with no MCA data');
          return;
        }
        this.logger.debug(`Received mca:updated for MCA ${data.mca.id}, subscription mcaId: ${mcaId || 'all'}`);
        if (mcaId && data.mca.id !== mcaId) {
          this.logger.debug(`Discarding mca:updated for MCA ${data.mca.id} (does not match ${mcaId})`);
          return;
        }
        callbacks.onUpdated!(data);
      };
      socket.on('mca:updated', handler);
      mcaListeners.set('mca:updated', handler);
    }

    if (callbacks.onDeleted) {
      const handler = (data: McaEvent) => {
        if (!data?.mca) return;
        if (mcaId && data.mca.id !== mcaId) return;
        callbacks.onDeleted!(data);
      };
      socket.on('mca:deleted', handler);
      mcaListeners.set('mca:deleted', handler);
    }
  }

  private removeListeners(subscriptionKey: string): void {
    if (!this.socket || !this.listeners.has(subscriptionKey)) return;

    const mcaListeners = this.listeners.get(subscriptionKey)!;
    for (const [event, handler] of mcaListeners.entries()) {
      this.socket.off(event, handler as any);
    }
    this.listeners.delete(subscriptionKey);
  }
}

const mcaSocketService = new McaSocketService();
export default mcaSocketService;